import { useState } from "react";
import { isGettingCode, isSuccess, PortInStep } from "../portInStep";
import { getVerificationCode } from "./getVerificationCodeApi";
import { PhoneNumberView } from "./phoneNumberView";
import { validateMobileNumber } from "./validateMobileNumberApi";
import { IMsisdn, INetwork, IPortinForm } from "../../IPortinForm";
import { useTranslation } from "react-i18next";
import { UseFormMethods, useWatch } from "react-hook-form";

interface IParams {
    phoneNumberSelected: string;
    formMethods: UseFormMethods<IPortinForm>;
    hasTooManyAttempt: boolean;
    portInStep: PortInStep;
    previousPhoneNumber?: string;
    variantStyleButton: "outlined" | "contained";
    setIsFinished(newValue: boolean): void;
    setOtpId(otpId: string): void;
    updatePortInStep(nextStep: PortInStep): void;
    setCurrentProvider(currentProvider: INetwork): void;
    setHasTooManyAttempt(hasTooManyAttempt: boolean): void;
    setPhoneNumberSelected(phoneNumber: string): void;
}

export const PhoneNumber = (props: IParams) => {
    const {
        variantStyleButton,
        updatePortInStep,
        setIsFinished,
        portInStep,
        previousPhoneNumber,
        setOtpId,
        formMethods,
        setCurrentProvider,
        hasTooManyAttempt,
        setHasTooManyAttempt,
        setPhoneNumberSelected,
        phoneNumberSelected,
    } = props;

    const { t } = useTranslation(["common", "portin"]);

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const changeValue = (newPhoneNumber: string) => {
        const cleanValue = newPhoneNumber.replace(/\s/g, "");
        setHasTooManyAttempt(false);
        formMethods.setValue("phoneNumber", cleanValue);
    };

    const phoneNumberValue = useWatch<string>({
        name: "phoneNumber",
        control: formMethods.control,
        defaultValue: "",
    });

    const getCode = async () => {
        setErrorMessage(undefined);
        if (await formMethods.trigger("phoneNumber")) {
            if (previousPhoneNumber && previousPhoneNumber === phoneNumberValue) {
                updatePortInStep(PortInStep.successCode);
                setIsFinished(true);
                setPhoneNumberSelected(phoneNumberValue);
            } else {
                setIsLoading(true);

                validateMobileNumber(phoneNumberValue, setErrorMessage)
                    .then((result: IMsisdn) => {
                        if (result) {
                            if (result.isPortInProgressAlready) {
                                setErrorMessage(t("portin:portInInProgress"));
                                setIsLoading(false);
                            } else {
                                setCurrentProvider(result.network);
                                getOtpIdCode();
                            }
                        } else {
                            getOtpIdCode();
                        }
                    })
                    .catch(() => {
                        getOtpIdCode();
                    });
            }
        }
    };

    const getOtpIdCode = () => {
        getVerificationCode(phoneNumberValue, setErrorMessage)
            .then((response) => {
                if (response) {
                    setOtpId(response.otpUuid);
                    updatePortInStep(PortInStep.verifyCode);
                    setPhoneNumberSelected(phoneNumberValue);
                }
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                setErrorMessage(t("common:genericError"));
            });
    };

    return (
        <PhoneNumberView
            changeValue={changeValue}
            errorMessage={errorMessage}
            formMethods={formMethods}
            getCode={getCode}
            hasTooManyAttempt={hasTooManyAttempt}
            isGettingCode={isGettingCode(portInStep)}
            isLoading={isLoading}
            isSuccess={isSuccess(portInStep)}
            phoneNumberSelected={phoneNumberSelected}
            previousPhoneNumber={previousPhoneNumber}
            variantStyleButton={variantStyleButton}
        />
    );
};
